import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Toast notification system — Variant B (detail card) per the mockup
// pick. Two surfaces:
//
//   * `<ToastContainer />` — mounted ONCE in App.tsx. Owns the toast
//     list + renders the bottom-right stack.
//   * `notify.{success,error,warn,info}(title, description?)` — global
//     function callable from any module (React component, utility,
//     hook, even non-React code). No Provider required, no
//     `useToast()` hook to thread through props. The container
//     subscribes to a tiny module-level event bus.
//
// Why a module bus instead of Context: the codebase has ~93 alert()
// call sites across 23 files including non-React utilities (e.g.
// lib/safeUrl.ts). A Context-based hook would force every caller to
// be a component — half of these are functions called from event
// handlers. The bus lets us drop-in replace with zero hierarchy
// changes.
//
// Each toast is a detail card with: colored left rail (by kind),
// circular icon badge, title + optional description, dismiss X, and
// a progress bar that ticks down to zero so the user sees the
// remaining lifetime. Lifetime defaults to 5 s (errors get 7 s
// since they often need to be read).
import { useCallback, useEffect, useRef, useState } from "react";
const listeners = new Set();
let nextId = 1;
function publish(t) {
    if (listeners.size === 0) {
        // No container mounted yet (shouldn't happen in normal flow, but
        // safe-fallback during early SSR/test): log to console so the
        // message isn't lost.
        // eslint-disable-next-line no-console
        console.warn("[toast] no container mounted — message dropped:", t.title, t.description ?? "");
        return;
    }
    for (const fn of listeners)
        fn(t);
}
function defaultDuration(kind) {
    // Errors get a longer read window — they usually carry a server
    // message the user needs to actually parse. Warnings are similar.
    // Success + info are quick confirmations and dismiss fast.
    if (kind === "error")
        return 7000;
    if (kind === "warn")
        return 6000;
    return 4500;
}
/** Global notify API — drop-in replacement for alert(). */
export const notify = {
    success(title, description, durationMs) {
        publish({ title, description, kind: "success", durationMs });
    },
    error(title, description, durationMs) {
        publish({ title, description, kind: "error", durationMs });
    },
    warn(title, description, durationMs) {
        publish({ title, description, kind: "warn", durationMs });
    },
    info(title, description, durationMs) {
        publish({ title, description, kind: "info", durationMs });
    },
};
const KIND_ICONS = {
    success: "✓",
    error: "!",
    warn: "!",
    info: "i",
};
function ToastCard({ toast, onDismiss, }) {
    // Auto-dismiss via a single timeout. We use a ref so a parent re-
    // render doesn't reset the clock. The CSS progress-bar animation
    // uses the same duration via inline style.
    const timeoutRef = useRef(null);
    useEffect(() => {
        timeoutRef.current = window.setTimeout(() => onDismiss(), toast.durationMs);
        return () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, [toast.durationMs, onDismiss]);
    return (_jsxs("div", { className: `toast-card toast-${toast.kind}`, role: toast.kind === "error" ? "alert" : "status", children: [_jsx("div", { className: "toast-icon", "aria-hidden": "true", children: KIND_ICONS[toast.kind] }), _jsxs("div", { className: "toast-body", children: [_jsx("div", { className: "toast-title", children: toast.title }), toast.description && (_jsx("div", { className: "toast-desc", children: toast.description }))] }), _jsx("button", { type: "button", className: "toast-close", onClick: onDismiss, "aria-label": "Dismiss", children: "\u00D7" }), _jsx("div", { className: "toast-progress", style: { animationDuration: `${toast.durationMs}ms` } })] }));
}
export default function ToastContainer() {
    const [toasts, setToasts] = useState([]);
    // Subscribe ON MOUNT to the module-level publisher set. Newest
    // toast lands on top so the most recent message is always visible.
    useEffect(() => {
        const listener = (t) => {
            const record = {
                id: nextId++,
                title: t.title,
                description: t.description ?? "",
                kind: t.kind,
                durationMs: t.durationMs ?? defaultDuration(t.kind),
            };
            setToasts((prev) => [record, ...prev].slice(0, 6));
        };
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, []);
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: "toast-container", role: "region", "aria-label": "Notifications", children: toasts.map((t) => (_jsx(ToastCard, { toast: t, onDismiss: () => dismiss(t.id) }, t.id))) }));
}
